// Service layer for Projects CRUD operations
import { supabase } from '../lib/supabase';

// Ensure user_profiles row exists for the current user.
// The handle_new_user() trigger should create it automatically, but if it
// didn't fire (e.g. email-confirmed users created before the trigger existed)
// the projects FK will fail. This upsert is a safe no-op when the row exists.
async function ensureUserProfile(user) {
  const { error } = await supabase
    ?.from('user_profiles')
    ?.upsert(
      {
        id: user?.id,
        email: user?.email,
        full_name:
          user?.user_metadata?.full_name ||
          user?.email?.split('@')?.[0] ||
          'User',
        avatar_url: user?.user_metadata?.avatar_url || '',
        role: user?.user_metadata?.role || 'buyer',
      },
      { onConflict: 'id', ignoreDuplicates: true }
    );

  if (error) {
    // Non-fatal: log but don't throw — the row may already exist
    console.warn('ensureUserProfile upsert warning (non-fatal):', error?.message);
  }
}

export const projectService = {
  // Get all projects for current user
  async getAll() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('projects')?.select(`
        *,
        project_vendors(
          vendor_id,
          vendors(
            id,
            name,
            company_name,
            rating
          )
        ),
        quotes(
          id,
          status,
          total_amount
        )
      `)?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

    if (error) throw error;

    // Convert snake_case to camelCase
    return data?.map(project => ({
      id: project?.id,
      userId: project?.user_id,
      name: project?.name,
      category: project?.category,
      status: project?.status,
      description: project?.description,
      budgetMin: project?.budget_min,
      budgetMax: project?.budget_max,
      deadline: project?.deadline,
      location: project?.location,
      specifications: project?.specifications,
      photos: project?.photos,
      createdAt: project?.created_at,
      updatedAt: project?.updated_at,
      vendors: project?.project_vendors?.map(pv => pv?.vendors) || [],
      quotes: project?.quotes || [],
      vendorCount: project?.project_vendors?.length || 0,
      quotesReceived: project?.quotes?.filter(q => q?.status === 'submitted')?.length || 0
    }));
  },

  // Get single project by ID
  async getById(projectId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('projects')?.select(`
        *,
        project_vendors(
          vendor_id,
          vendors(*)
        ),
        quotes(*),
        milestones(*),
        questionnaires(*)
      `)?.eq('id', projectId)?.eq('user_id', user?.id)?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      name: data?.name,
      category: data?.category,
      status: data?.status,
      description: data?.description,
      budgetMin: data?.budget_min,
      budgetMax: data?.budget_max,
      deadline: data?.deadline,
      location: data?.location,
      specifications: data?.specifications,
      photos: data?.photos,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at,
      vendors: data?.project_vendors?.map(pv => pv?.vendors) || [],
      quotes: data?.quotes || [],
      milestones: data?.milestones || [],
      questionnaires: data?.questionnaires || []
    };
  },

  // Create new project
  async create(projectData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Guarantee user_profiles row exists before inserting (FK guard)
    await ensureUserProfile(user);

    const { data, error } = await supabase?.from('projects')?.insert({
        user_id: user?.id,
        name: projectData?.name,
        category: projectData?.category,
        status: projectData?.status || 'draft',
        description: projectData?.description,
        budget_min: projectData?.budgetMin,
        budget_max: projectData?.budgetMax,
        deadline: projectData?.deadline,
        location: projectData?.location,
        specifications: projectData?.specifications || {},
        photos: projectData?.photos || []
      })?.select()?.single();

    if (error) {
      console.error('projectService.create error:', error?.code, error?.message, error?.details);
      throw error;
    }

    return {
      id: data?.id,
      userId: data?.user_id,
      name: data?.name,
      category: data?.category,
      status: data?.status,
      createdAt: data?.created_at
    };
  },

  // Update project
  async update(projectId, projectData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const updateData = {};
    if (projectData?.name !== undefined) updateData.name = projectData?.name;
    if (projectData?.category !== undefined) updateData.category = projectData?.category;
    if (projectData?.status !== undefined) updateData.status = projectData?.status;
    if (projectData?.description !== undefined) updateData.description = projectData?.description;
    if (projectData?.budgetMin !== undefined) updateData.budget_min = projectData?.budgetMin;
    if (projectData?.budgetMax !== undefined) updateData.budget_max = projectData?.budgetMax;
    if (projectData?.deadline !== undefined) updateData.deadline = projectData?.deadline;
    if (projectData?.location !== undefined) updateData.location = projectData?.location;
    if (projectData?.specifications !== undefined) updateData.specifications = projectData?.specifications;
    if (projectData?.photos !== undefined) updateData.photos = projectData?.photos;

    const { data, error } = await supabase?.from('projects')?.update(updateData)?.eq('id', projectId)?.eq('user_id', user?.id)?.select()?.single();

    if (error) throw error;
    return data;
  },

  // Delete project
  async delete(projectId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase?.from('projects')?.delete()?.eq('id', projectId)?.eq('user_id', user?.id);

    if (error) throw error;
  },

  // Get single project by ID without requiring authentication (for vendor landing page)
  async getByIdPublic(projectId) {
    const { data, error } = await supabase
      ?.from('projects')
      ?.select(`
        *,
        user_profiles!projects_user_id_fkey(full_name),
        project_vendors(id)
      `)
      ?.eq('id', projectId)
      ?.single();

    if (error) throw error;

    const specs = data?.specifications || {};
    const visibleRequirements = specs?.needs
      ? (Array.isArray(specs?.needs) ? specs?.needs : Object.values(specs?.needs))?.slice(0, 4)
      : specs?.requirements
        ? (Array.isArray(specs?.requirements) ? specs?.requirements : [])?.slice(0, 4)
        : [];

    return {
      id: data?.id,
      title: data?.name,
      category: data?.category?.replace(/_/g, ' ')?.replace(/\b\w/g, c => c?.toUpperCase()) || 'General',
      timeline: data?.deadline
        ? `Due ${new Date(data.deadline)?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
        : 'Timeline not specified',
      location: data?.location || 'Location not specified',
      clientName: data?.user_profiles?.full_name
        ? data?.user_profiles?.full_name?.split(' ')?.[0] + ' ' + (data?.user_profiles?.full_name?.split(' ')?.[1]?.[0] || '') + '.'
        : 'Client',
      requirements: {
        visible: visibleRequirements?.length > 0
          ? visibleRequirements
          : [data?.description || 'See full project details after registration'],
        hidden: specs?.additionalNotes
          ? [specs?.additionalNotes]
          : ['Additional project details available after registration']
      },
      postedDate: data?.created_at ? data?.created_at?.split('T')?.[0] : new Date()?.toISOString()?.split('T')?.[0],
      responseDeadline: data?.deadline || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0],
      vendorCount: data?.project_vendors?.length || 0,
      description: data?.description,
      specifications: data?.specifications || {},
      budgetMin: data?.budget_min,
      budgetMax: data?.budget_max,
      photos: data?.photos || [],
    };
  },

  // Subscribe to real-time changes
  subscribeToChanges(callback) {
    const channel = supabase?.channel('projects_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'projects'
        },
        (payload) => {
          callback(payload);
        }
      )?.subscribe();

    return () => supabase?.removeChannel(channel);
  }
};
